"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Plus,
  Minus,
  Trash2,
  Send,
  MessageSquare,
  ChefHat,
  HandCoins,
  CheckCircle2,
  MoveHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import type {
  Category,
  Order,
  OrderItem,
  Product,
  ReservationSendMode,
  Table,
  User,
} from "@/lib/pos-types";
import { cn, formatPrice } from "@/lib/utils";
import { toast } from "sonner";
import { EditDishPriceModal } from "@/components/pos/EditDishPriceModal";

interface OrderPanelProps {
  takeawayOrder?: Order | null;
  table?: Table | null;
  reservationId?: string;
  reservationSendMode?: ReservationSendMode;
  onClose: () => void;
  categories: Category[];
  products: Product[];
  orders: Order[];
  currentUser: User;
  onSaveOrder?: (table: Table | null | undefined, items: OrderItem[]) => void;
  onSendToKitchen?: (table: Table | null, items: OrderItem[]) => void;
  sendToKitchenDisabledReason?: string;
  onRequestReservationSend?: (reservationId: string, orderId: string) => void;
  onUpdateOrderItems?: (orderId: string, items: OrderItem[]) => void;
  onRequestPayment?: (orderId: string) => void;
  onTransferTable?: (table: Table) => void;
  onDeleteOrder?: (orderId: string) => void;
}

export function OrderPanel({
  takeawayOrder,
  table,
  reservationId,
  reservationSendMode,
  onClose,
  categories,
  products,
  orders,
  currentUser,
  onSaveOrder,
  onSendToKitchen,
  sendToKitchenDisabledReason,
  onRequestReservationSend,
  onUpdateOrderItems,
  onRequestPayment,
  onTransferTable,
  onDeleteOrder,
}: OrderPanelProps) {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]?.id);
  const [productSearch, setProductSearch] = useState("");
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);

  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editingPriceItem, setEditingPriceItem] = useState<OrderItem | null>(null);
  const [noteText, setNoteText] = useState("");
  const isTakeaway = Boolean(takeawayOrder);
  const [isSending, setIsSending] = useState(false);
  const currentEntityKey = takeawayOrder
    ? `takeaway-${takeawayOrder.id}`
    : reservationId
    ? `reservation-${reservationId}`
    : table
    ? `table-${table.id}`
    : "none";

  const existingOrder = useMemo(() => {
    if (takeawayOrder) {
      return takeawayOrder;
    }

    return (
      orders.find(
        (o) =>
          (reservationId
            ? o.reservationId === reservationId
            : table?.id
            ? o.tableId === table.id &&
              (table?.currentOrderId
                ? o.id === table.currentOrderId
                : !o.reservationId)
            : false) &&
          o.status !== "paid" &&
          o.status !== "closed",
      ) ?? null
    );
  }, [orders, reservationId, table?.id, takeawayOrder, table?.currentOrderId]);

  const isEditLocked = Boolean(
    existingOrder &&
    existingOrder.status !== "open" &&
    currentUser?.role !== "admin",
  );
  const isLocked = isEditLocked;
  const isOrderOpen = !existingOrder || existingOrder.status === "open";
  const canSendToKitchen = !existingOrder || ["open", "in-kitchen", "ready", "delivered"].includes(existingOrder.status);
  const canAdminAdjust = Boolean(
    existingOrder &&
    existingOrder.status !== "open" &&
    existingOrder.status !== "closed" &&
    currentUser?.role === "admin",
  );

  const visibleOrderItems = useMemo(() => {
    return orderItems.filter((i) => i.status !== "cancelled");
  }, [orderItems]);

  const categoryProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return products.filter((p) => {
      if (!p.available) return false;
      if (!q && p.categoryId !== selectedCategory) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [productSearch, products, selectedCategory]);
  const productsById = useMemo(
    () => new Map(products.map((p) => [p.id, p] as const)),
    [products],
  );

  const getItemStatusLabel = (status: OrderItem["status"]) => {
    switch (status) {
      case "pending":
        return "Pendiente";
      case "sent":
        return "En cocina";
      case "in-progress":
        return "En preparación";
      case "ready":
        return "Listo";
      case "delivered":
        return "Entregado";
      case "cancelled":
        return "Anulado";
      default:
        return status;
    }
  };

  const addProduct = (product: Product) => {
    if (isEditLocked) {
      toast.error("La comanda ya fue enviada a cocina y no puede modificarse");
      return;
    }

    if (!product.stockAvailable) {
      toast.warning(`Atención: Sin stock suficiente para ${product.name} (Venta sin stock)`);
    }

    const baseItems = orderItems.length > 0 ? orderItems : (existingOrder?.items || []);

    // Informar límite de porciones si el producto tiene receta con stock limitado
    if (product.stockPortions !== null) {
      const pendingQty = baseItems
        .filter((i) => i.productId === product.id && i.status === "pending")
        .reduce((sum, i) => sum + i.quantity, 0);

      if (pendingQty >= product.stockPortions) {
        toast.warning(
          product.stockPortions === 0
            ? `Atención: Sin stock suficiente para ${product.name} (Venta sin stock)`
            : `Atención: Quedan ${product.stockPortions} porción(es) de ${product.name} (Venta sin stock)`
        );
      }
    }

    const existing = baseItems.find(
      (i) => i.productId === product.id && !i.notes && i.status === "pending",
    );

    let nextItems: OrderItem[];
    if (existing) {
      nextItems = baseItems.map((i) =>
        i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i,
      );
    } else {
      const initialStatus: OrderItem["status"] = "pending";

      const newItem: OrderItem = {
        id: `temp-${crypto.randomUUID()}`,
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitPrice: product.price,
        status: initialStatus,
        addedAt: new Date(),
      };

      nextItems = [...baseItems, newItem];
    }

    isUserEditedRef.current = true;
    setOrderItems(nextItems);
    onSaveOrder?.(table, nextItems);
    toast.success(`${product.name} agregado`);
  };

  const updateQuantity = (itemId: string, delta: number) => {
    if (isEditLocked) {
      toast.error("La comanda ya fue enviada a cocina y no puede modificarse");
      return;
    }

    const baseItems = orderItems.length > 0 ? orderItems : (existingOrder?.items || []);
    const targetItem = baseItems.find((i) => i.id === itemId);
    if (!targetItem) return;

    if (delta > 0 && targetItem.status !== "pending") {
      // Si el item no está pendiente, agregar uno nuevo pendiente del mismo producto
      const existingPending = baseItems.find(
        (i) => i.productId === targetItem.productId && !i.notes && i.status === "pending"
      );

      // Informar límite de porciones antes de agregar
      const product = productsById.get(targetItem.productId);
      if (product?.stockPortions !== undefined && product.stockPortions !== null) {
        const pendingQty = baseItems
          .filter((i) => i.productId === targetItem.productId && i.status === "pending")
          .reduce((sum, i) => sum + i.quantity, 0);
        if (pendingQty >= product.stockPortions) {
          toast.warning(`Atención: Quedan ${product.stockPortions} porción(es) disponibles de ${targetItem.productName} (Venta sin stock)`);
        }
      }

      let nextItems: OrderItem[];
      if (existingPending) {
        nextItems = baseItems.map((i) =>
          i.id === existingPending.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      } else {
        const newItem: OrderItem = {
          id: `temp-${crypto.randomUUID()}`,
          productId: targetItem.productId,
          productName: targetItem.productName,
          quantity: 1,
          unitPrice: targetItem.unitPrice,
          status: "pending",
          addedAt: new Date(),
        };
        nextItems = [...baseItems, newItem];
      }
      setOrderItems(nextItems);
      onSaveOrder?.(table, nextItems);
      toast.success(`${targetItem.productName} agregado`);
      return;
    }

    // Informar límite de porciones al incrementar un item pendiente
    if (delta > 0 && targetItem.status === "pending") {
      const product = productsById.get(targetItem.productId);
      if (product?.stockPortions !== undefined && product.stockPortions !== null) {
        const pendingQty = baseItems
          .filter((i) => i.productId === targetItem.productId && i.status === "pending")
          .reduce((sum, i) => sum + i.quantity, 0);
        if (pendingQty >= product.stockPortions) {
          toast.warning(`Atención: Quedan ${product.stockPortions} porción(es) disponibles de ${targetItem.productName} (Venta sin stock)`);
        }
      }
    }

    const nextItems = baseItems.map((item) => {
      if (item.id === itemId) {
        return { ...item, quantity: Math.max(1, item.quantity + delta) };
      }
      return item;
    });

    isUserEditedRef.current = true;
    setOrderItems(nextItems);
    onSaveOrder?.(table, nextItems);
  };

  const removeItem = (itemId: string) => {
    if (isEditLocked) {
      toast.error("La comanda ya fue enviada a cocina y no puede modificarse");
      return;
    }
    const baseItems = orderItems.length > 0 ? orderItems : (existingOrder?.items || []);
    const nextItems = baseItems.filter((i) => i.id !== itemId);
    isUserEditedRef.current = true;
    setOrderItems(nextItems);
    onSaveOrder?.(table, nextItems);
    toast.info("Producto eliminado");
  };

  const saveNote = (itemId: string) => {
    if (isEditLocked) {
      toast.error("La comanda ya fue enviada a cocina y no puede modificarse");
      return;
    }
    const trimmed = noteText.trim();
    if (trimmed.length > 120) {
      toast.error("La nota no puede superar 120 caracteres");
      return;
    }
    const baseItems = orderItems.length > 0 ? orderItems : (existingOrder?.items || []);
    const nextItems = baseItems.map((i) => (i.id === itemId ? { ...i, notes: trimmed } : i));
    isUserEditedRef.current = true;
    setOrderItems(nextItems);
    onSaveOrder?.(table, nextItems);
    setEditingNote(null);
    setNoteText("");
  };

  const total = visibleOrderItems.reduce(
    (sum, i) => sum + i.quantity * i.unitPrice,
    0,
  );

  const subtotal = Math.round(total / 1.19);
  const tax = total - subtotal;
  const hasItemsOrAdjustments = existingOrder && canAdminAdjust
    ? orderItems.length > 0 || existingOrder.items.length > 0
    : visibleOrderItems.length > 0;

  const isSendToKitchenDisabled =
    !hasItemsOrAdjustments ||
    !canSendToKitchen ||
    !onSendToKitchen ||
    isSending ||
    Boolean(sendToKitchenDisabledReason);

  const handleSendToKitchen = () => {
    if (isSending) return;

    if (sendToKitchenDisabledReason) {
      toast.error("No disponible", {
        description: sendToKitchenDisabledReason,
      });
      return;
    }

    const itemsToSend =
      existingOrder && canAdminAdjust
        ? buildAdminAdjustedItems(existingOrder.items, orderItems)
        : visibleOrderItems;

    if (itemsToSend.length === 0) {
      toast.error("La comanda debe tener al menos un producto");
      return;
    }

    if (!canSendToKitchen) {
      toast.error("La comanda ya no puede enviarse a cocina");
      return;
    }

    setIsSending(true);
    setTimeout(() => setIsSending(false), 2000);

    isUserEditedRef.current = false;
    onSendToKitchen?.(table ?? null, itemsToSend);
  };

  const handleRequestReservationSend = () => {
    if (!reservationId) return;
    if (reservationSendMode !== "manual") return;
    if (!existingOrder) {
      toast.error("Primero guarda la comanda");
      return;
    }
    onRequestReservationSend?.(reservationId, existingOrder.id);
  };

  const handleDeliverItem = (itemId: string) => {
    if (!existingOrder) return;
    const nextItems: OrderItem[] = existingOrder.items.map((i) =>
      i.id === itemId ? { ...i, status: "delivered" as const } : i,
    );
    onUpdateOrderItems?.(existingOrder.id, nextItems);
    toast.success("Producto entregado");
  };

  const buildAdminAdjustedItems = (
    original: OrderItem[],
    edited: OrderItem[],
  ): OrderItem[] => {
    const editedById = new Map(edited.map((i) => [i.id, i] as const));
    const usedEditedIds = new Set<string>();
    const out: OrderItem[] = [];

    for (const orig of original) {
      const ed = editedById.get(orig.id) ?? null;
      if (!ed) {
        if (orig.status === "delivered") {
          out.push(orig);
          continue;
        }

        if (orig.status === "pending") {
          continue;
        }

        if (orig.status === "cancelled") {
          out.push(orig);
          continue;
        }

        out.push({ ...orig, status: "cancelled" as const });
        continue;
      }
      usedEditedIds.add(ed.id);
      const changed =
        ed.productId !== orig.productId ||
        ed.quantity !== orig.quantity ||
        (ed.notes ?? "") !== (orig.notes ?? "");

      if (!changed) {
        out.push(orig);
        continue;
      }

      if (orig.status === "delivered") {
        out.push(orig);
        continue;
      }

      if (orig.status === "pending") {
        const prod = productsById.get(ed.productId);
        out.push({
          ...orig,
          productId: ed.productId,
          productName: prod?.name ?? ed.productName,
          quantity: ed.quantity,
          unitPrice: prod?.price ?? ed.unitPrice,
          notes: ed.notes,
          status: "pending",
        });
        continue;
      }

      out.push({ ...orig, status: "cancelled" as const });
      const prod = productsById.get(ed.productId);
      out.push({
        id: `temp-${crypto.randomUUID()}`,
        productId: ed.productId,
        productName: prod?.name ?? ed.productName,
        quantity: ed.quantity,
        unitPrice: prod?.price ?? ed.unitPrice,
        notes: ed.notes,
        status: "sent",
        addedAt: new Date(),
      });
    }

    for (const ed of edited) {
      if (usedEditedIds.has(ed.id)) continue;
      const prod = productsById.get(ed.productId);
      out.push({
        ...ed,
        productName: prod?.name ?? ed.productName,
        unitPrice: prod?.price ?? ed.unitPrice,
        status: canAdminAdjust ? "sent" : (ed.status ?? "pending"),
        addedAt: ed.addedAt ?? new Date(),
      });
    }

    return out;
  };



  const handleRequestPayment = () => {
    if (!existingOrder) return;
    onRequestPayment?.(existingOrder.id);
  };



  const lastEntityKeyRef = useRef(currentEntityKey);
  const isUserEditedRef = useRef(false);

  useEffect(() => {
    if (lastEntityKeyRef.current !== currentEntityKey) {
      lastEntityKeyRef.current = currentEntityKey;
      isUserEditedRef.current = false;
      setOrderItems(existingOrder?.items || []);
      setEditingNote(null);
      setEditingPriceItem(null);
      setNoteText("");
      return;
    }

    if (!isUserEditedRef.current) {
      setOrderItems(existingOrder?.items || []);
    } else if (existingOrder?.items) {
      const localCount = orderItems.length;
      const serverCount = existingOrder.items.length;
      if (serverCount >= localCount) {
        setOrderItems(existingOrder.items);
        isUserEditedRef.current = false;
      }
    }
  }, [currentEntityKey, existingOrder]);

  return (
    <div className="w-full sm:w-[520px] max-w-[100vw] flex flex-col bg-card border border-border sm:rounded-xl overflow-hidden animate-in slide-in-from-right-5 duration-300 h-full sm:h-auto shadow-2xl z-50 fixed inset-0 sm:relative sm:inset-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30 shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
            <span className="font-bold text-[11px] sm:text-xs">
              {isTakeaway
                ? (takeawayOrder?.clientName || "L").charAt(0).toUpperCase()
                : table
                ? `M${table.number}`
                : existingOrder?.clientName
                ? existingOrder.clientName.charAt(0).toUpperCase()
                : "R"}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-sm sm:text-base text-foreground">
              {isTakeaway
                ? (takeawayOrder?.clientName || "Para llevar")
                : table
                ? `Mesa ${table.number}`
                : existingOrder?.clientName
                ? `Reserva: ${existingOrder.clientName}`
                : "Reserva Sin Mesa"}
            </h3>
            <p className="text-[10px] text-muted-foreground hidden sm:block">
              Mesero: {existingOrder?.waiter ?? currentUser?.name ?? "Mesero"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] sm:hidden">
            {visibleOrderItems.length} items
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Content - Two Columns/Tabs for Mobile */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left: Categories + Products */}
        <div className="flex-1 flex flex-col border-r border-border min-w-0 min-h-0">
          {/* Categories */}
          <div className="flex gap-1 p-2 border-b border-border overflow-x-auto no-scrollbar shrink-0">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors",
                  selectedCategory === cat.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground hover:text-foreground",
                )}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <div className="p-2 border-b border-border shrink-0">
            <Input
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Buscar producto..."
              onFocus={() => setProductSearch("")}
              className="h-8 text-xs"
            />
          </div>

          {/* Products Grid */}
          <ScrollArea className="flex-1 p-2 min-h-0">
            <div className="grid grid-cols-2 gap-2 pb-4">
              {categoryProducts.length === 0 && (
                <div className="col-span-2 text-center py-6 text-[11px] text-muted-foreground">
                  Sin productos para tu búsqueda
                </div>
              )}
              {categoryProducts.map((product) => {
                const LOW_STOCK_THRESHOLD = 4;
                const isLowStock =
                  product.stockPortions !== null &&
                  product.stockPortions > 0 &&
                  product.stockPortions <= LOW_STOCK_THRESHOLD;

                return (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product)}
                    disabled={isLocked}
                    className={cn(
                      "flex flex-col p-3 rounded-lg border transition-all text-left group relative",
                      isLocked
                        ? "opacity-50 cursor-not-allowed bg-muted/20 border-border/30"
                        : product.stockAvailable
                          ? "bg-muted/30 hover:bg-muted/50 border-transparent hover:border-primary/30 cursor-pointer"
                          : "bg-muted/20 hover:bg-muted/50 border-amber-500/30 cursor-pointer"
                    )}
                  >
                    {/* Badge de sin stock */}
                    {!product.stockAvailable && (
                      <span className="text-[9px] font-semibold text-destructive uppercase tracking-wider mb-1">
                        Sin stock
                      </span>
                    )}
                    {/* Badge de stock bajo (quedan pocas porciones) */}
                    {isLowStock && (
                      <span className="text-[9px] font-semibold text-amber-500 uppercase tracking-wider mb-1">
                        ⚠️ {product.stockPortions} disp.
                      </span>
                    )}
                    <span className="text-xs sm:text-sm font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                      {product.name}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
                      {product.description}
                    </span>
                    <span className="mt-2 text-xs sm:text-sm font-semibold text-primary">
                      {formatPrice(product.price)}
                    </span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Right: Current Order - Hide on tiny screens if we wanted, but here we make it slim */}
        <div className="hidden sm:flex w-[220px] flex-col border-l border-border bg-muted/5  min-h-0">
          <div className="px-3 py-2 border-b border-border shrink-0">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              Pedido Actual
            </span>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="p-2 space-y-2">
              {visibleOrderItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                  <ChefHat className="w-8 h-8 text-muted-foreground/30 mb-2" />
                  <span className="text-[10px] text-muted-foreground">
                    Toca un producto para agregarlo
                  </span>
                </div>
              ) : (
                visibleOrderItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-2 rounded-lg bg-muted/30 border border-border group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-foreground line-clamp-1">
                          {item.productName}
                        </span>
                        {item.notes && (
                          <div className="mt-1 p-1 rounded bg-amber-500/10 border border-amber-500/20">
                            <span className="text-[10px] text-amber-400 font-medium italic block whitespace-pre-wrap break-words">
                              📝 {item.notes}
                            </span>
                          </div>
                        )}
                        {existingOrder && existingOrder.status !== "open" && (
                          <span
                            className={cn(
                              "inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold uppercase tracking-wider mt-1 border",
                              item.status === "pending" && "bg-slate-500/10 text-slate-400 border-slate-500/20",
                              (item.status === "sent" || item.status === "in-progress") && "bg-amber-500/10 text-amber-500 border-amber-500/20",
                              item.status === "ready" && "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
                              item.status === "delivered" && "bg-slate-500/5 text-slate-500 border-slate-500/10 opacity-70",
                              item.status === "cancelled" && "bg-red-500/10 text-red-500 border-red-500/20",
                            )}
                          >
                            {getItemStatusLabel(item.status)}
                          </span>
                        )}
                      </div>
                      {currentUser?.role === "admin" ? (
                        <button
                          type="button"
                          onClick={() => setEditingPriceItem(item)}
                          className="px-1.5 py-0.5 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[10px] font-mono font-medium shrink-0 flex items-center gap-1 transition-colors"
                          title="Modificar precio de plato (Admin)"
                        >
                          <span>{formatPrice(item.quantity * item.unitPrice)}</span>
                          <span className="text-[8px]">✏️</span>
                        </button>
                      ) : (
                        <span className="text-[10px] font-medium text-muted-foreground shrink-0">
                          {formatPrice(item.quantity * item.unitPrice)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5 h-min"
                          disabled={isLocked}
                          onClick={() => updateQuantity(item.id, -1)}
                        >
                          <Minus className="w-2.5 h-2.5" />
                        </Button>
                        <span className="w-4 text-center text-[11px] font-bold">
                          {item.quantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5"
                          disabled={isLocked}
                          onClick={() => updateQuantity(item.id, 1)}
                        >
                          <Plus className="w-2.5 h-2.5" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        {existingOrder?.status === "ready" &&
                          item.status === "ready" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-5 h-5 text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleDeliverItem(item.id)}
                            >
                              <CheckCircle2 className="w-3 h-3" />
                            </Button>
                          )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5 text-muted-foreground hover:text-foreground"
                          disabled={isLocked}
                          onClick={() => {
                            setEditingNote(item.id);
                            setNoteText(item.notes || "");
                          }}
                        >
                          <MessageSquare className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5 text-destructive"
                          disabled={isLocked}
                          onClick={() => removeItem(item.id)}
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Note Editor */}
                    {editingNote === item.id && (
                      <div className="mt-2 flex gap-1">
                        <Input
                          autoFocus
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          onBlur={() => saveNote(item.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.currentTarget.blur();
                            }
                            if (e.key === "Escape") {
                              setEditingNote(null);
                              setNoteText("");
                            }
                          }}
                          placeholder="Nota..."
                          className="h-6 text-[10px]"
                          maxLength={120}
                        />
                        <Button
                          size="sm"
                          className="h-6 px-1.5 text-[10px]"
                          onClick={() => saveNote(item.id)}
                        >
                          Ok
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Order Summary & Actions */}
          <div className="p-3 border-t border-border bg-muted/10">
            <div className="space-y-1 mb-3">
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-[11px] font-bold text-foreground">
                <span>Total</span>
                <span className="text-primary">{formatPrice(total)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {reservationId && reservationSendMode === "manual" ? (
                <Button
                  className="w-full h-8 text-xs gap-1.5"
                  disabled={
                    visibleOrderItems.length === 0 ||
                    !isOrderOpen ||
                    !onRequestReservationSend
                  }
                  onClick={handleRequestReservationSend}
                >
                  <Send className="w-3.5 h-3.5" />
                  Solicitar envío
                </Button>
              ) : (
                <Button
                  className="w-full h-8 text-xs gap-1.5"
                  disabled={isSendToKitchenDisabled}
                  onClick={handleSendToKitchen}
                  title={sendToKitchenDisabledReason || undefined}
                >
                  <Send className="w-3.5 h-3.5" />
                  Cocina
                </Button>
              )}

              {currentUser?.role === "admin" && table && existingOrder && existingOrder.status !== "closed" && onTransferTable && (
                <Button
                  variant="outline"
                  className="w-full h-8 text-xs gap-1.5 border-amber-500/50 hover:bg-amber-500/10 text-amber-600 hover:text-amber-700 font-medium"
                  onClick={() => onTransferTable(table)}
                >
                  <MoveHorizontal className="w-3.5 h-3.5" />
                  Trasladar Mesa
                </Button>
              )}
              {currentUser?.role === "admin" && existingOrder && existingOrder.status !== "closed" && onDeleteOrder && (
                <Button
                  variant="destructive"
                  className="w-full h-8 text-xs gap-1.5 font-medium"
                  onClick={() => {
                    if (confirm("¿Está seguro de ANULAR y ELIMINAR esta comanda por completo? Esta acción es irreversible.")) {
                      onDeleteOrder(existingOrder.id);
                    }
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Anular / Eliminar Comanda
                </Button>
              )}
              {existingOrder &&
                existingOrder.status !== "open" &&
                existingOrder.status !== "pending-payment" &&
                existingOrder.status !== "closed" && (
                  <Button
                    variant="secondary"
                    className="w-full h-8 text-xs gap-1.5"
                    onClick={handleRequestPayment}
                  >
                    <HandCoins className="w-3.5 h-3.5" />
                    Solicitar cobro
                  </Button>
                )}
            </div>
          </div>
        </div>

        {/* Mobile Action Bar - ONLY SHOWN ON MOBILE */}
        <div className="sm:hidden absolute bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-md border-t border-border flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
              Total
            </span>
            <span className="text-lg font-bold text-primary leading-tight">
              {formatPrice(total)}
            </span>
          </div>
          <div className="flex gap-2">
            {reservationId && reservationSendMode === "manual" ? (
              <Button
                className="px-6 h-10 text-sm font-bold gap-2 shadow-lg shadow-primary/20"
                disabled={
                  visibleOrderItems.length === 0 ||
                  !isOrderOpen ||
                  !onRequestReservationSend
                }
                onClick={handleRequestReservationSend}
              >
                <Send className="w-4 h-4" />
                Solicitar envío
              </Button>
            ) : (
              <Button
                className="px-6 h-10 text-sm font-bold gap-2 shadow-lg shadow-primary/20"
                disabled={isSendToKitchenDisabled}
                onClick={handleSendToKitchen}
                title={sendToKitchenDisabledReason || undefined}
              >
                <Send className="w-4 h-4" />
                Cocina
              </Button>
            )}
            {currentUser.role === "admin" && table && existingOrder && existingOrder.status !== "closed" && onTransferTable && (
              <Button
                variant="outline"
                className="px-4 h-10 text-sm font-bold gap-2 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                onClick={() => onTransferTable(table)}
              >
                <MoveHorizontal className="w-4 h-4" />
                Trasladar
              </Button>
            )}
            {currentUser.role === "admin" && existingOrder && existingOrder.status !== "closed" && onDeleteOrder && (
              <Button
                variant="destructive"
                className="px-4 h-10 text-sm font-bold gap-2"
                onClick={() => {
                  if (confirm("¿Está seguro de ANULAR y ELIMINAR esta comanda por completo? Esta acción es irreversible.")) {
                    onDeleteOrder(existingOrder.id);
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
                Anular
              </Button>
            )}
            {existingOrder &&
              existingOrder.status !== "open" &&
              existingOrder.status !== "pending-payment" &&
              existingOrder.status !== "closed" && (
                <Button
                  variant="secondary"
                  className="px-4 h-10 text-sm font-bold gap-2"
                  onClick={handleRequestPayment}
                >
                  <HandCoins className="w-4 h-4" />
                  Cobro
                </Button>
              )}
          </div>
        </div>
      </div>

      {editingPriceItem && (
        <EditDishPriceModal
          isOpen={Boolean(editingPriceItem)}
          onClose={() => setEditingPriceItem(null)}
          onSave={(newUnitPrice) => {
            const baseItems = orderItems.length > 0 ? orderItems : (existingOrder?.items || []);
            const nextItems = baseItems.map((i) =>
              i.id === editingPriceItem.id ? { ...i, unitPrice: newUnitPrice } : i
            );
            setOrderItems(nextItems);
            if (existingOrder && (table || isTakeaway)) {
              if (existingOrder.id && onUpdateOrderItems) {
                onUpdateOrderItems(existingOrder.id, nextItems);
              } else {
                onSaveOrder?.(table, nextItems);
              }
            } else {
              onSaveOrder?.(table, nextItems);
            }
            toast.success(`Precio de ${editingPriceItem.productName} actualizado a ${formatPrice(newUnitPrice)}`);
            setEditingPriceItem(null);
          }}
          productName={editingPriceItem.productName}
          currentUnitPrice={editingPriceItem.unitPrice}
          catalogBasePrice={productsById.get(editingPriceItem.productId)?.price ?? editingPriceItem.unitPrice}
        />
      )}
    </div>
  );
}
